import {
    activityCategories,
    ActivityCategory,
    activityCategoryLabels,
    ActivityTarget,
    ActivityTargetView,
    allCategoryTargets,
    categoryActivityTarget,
    isAnySelected,
    serializeActivityTarget,
    taskTypeActivityTarget,
    TaskTypeId,
    unknownActivityTypeLabel,
} from "@sp-crm/core";

export const cadenceUnitInputs = ["Daily", "Weekly", "Monthly"] as const;

export type CadenceUnitInput = (typeof cadenceUnitInputs)[number];

export interface TouchpointCadence {
    id: string;
    tierListItemId: string;
    intervalUnit: string;
    intervalCount: number;
}

export interface EditableTouchpoint {
    targets: ActivityTarget[];
    intervalUnit: CadenceUnitInput;
    intervalCount: number;
}

export const emptyEditableTouchpoint = (): EditableTouchpoint => ({
    targets: [],
    intervalUnit: "Weekly",
    intervalCount: 1,
});

export const joinKindLabelsWithOr = (labels: string[]): string => {
    if (labels.length <= 1) {
        return labels.join("");
    }
    if (labels.length === 2) {
        return `${labels[0]} or ${labels[1]}`;
    }
    return `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`;
};

export const cadenceUnitOptions: { value: CadenceUnitInput; label: string }[] = [
    { value: "Daily", label: "days" },
    { value: "Weekly", label: "weeks" },
    { value: "Monthly", label: "months" },
];

export const asCadenceUnitInput = (value: string): CadenceUnitInput => {
    const match = cadenceUnitInputs.find(unit => unit === value);
    return match ?? "Weekly";
};

const singularUnitLabel: Record<CadenceUnitInput, string> = {
    Daily: "day",
    Weekly: "week",
    Monthly: "month",
};

export const describeInterval = (
    intervalCount: number,
    unit: CadenceUnitInput,
): string => {
    const singular = singularUnitLabel[unit];
    return intervalCount === 1
        ? `every ${singular}`
        : `every ${intervalCount} ${singular}s`;
};

export const summarizeSchedule = (
    kindLabels: string[],
    intervalCount: number,
    unit: CadenceUnitInput,
): string =>
    `${joinKindLabelsWithOr(kindLabels)} ${describeInterval(intervalCount, unit)}`;

export const targetKey = serializeActivityTarget;

export const targetsInclude = (
    targets: ActivityTarget[],
    target: ActivityTarget,
): boolean => targets.some(existing => targetKey(existing) === targetKey(target));

export const toggleAny = (targets: ActivityTarget[]): ActivityTarget[] =>
    isAnySelected(targets) ? [] : allCategoryTargets();

export interface ActivityTypeOption {
    taskTypeId: TaskTypeId;
    name: string;
    category: ActivityCategory;
    isArchived: boolean;
}

const byTypeName = (a: ActivityTypeOption, b: ActivityTypeOption): number =>
    a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());

const categoryOfType = (
    activityTypes: ActivityTypeOption[],
    taskTypeId: TaskTypeId,
): ActivityCategory | null =>
    activityTypes.find(option => option.taskTypeId === taskTypeId)?.category ?? null;

const categoryOfTarget = (
    target: ActivityTarget,
    activityTypes: ActivityTypeOption[],
): ActivityCategory | null =>
    target.kind === "category"
        ? target.category
        : categoryOfType(activityTypes, target.taskTypeId);

export const typeOptionsForCategory = (
    activityTypes: ActivityTypeOption[],
    category: ActivityCategory,
): ActivityTypeOption[] =>
    activityTypes
        .filter(option => option.category === category && !option.isArchived)
        .sort(byTypeName);

export const selectedCategories = (
    targets: ActivityTarget[],
    activityTypes: ActivityTypeOption[],
): ActivityCategory[] => {
    const selected = new Set(
        targets.flatMap(target => {
            const category = categoryOfTarget(target, activityTypes);
            return category ? [category] : [];
        }),
    );
    return activityCategories.filter(category => selected.has(category));
};

export const isWholeCategorySelected = (
    targets: ActivityTarget[],
    category: ActivityCategory,
): boolean => targetsInclude(targets, categoryActivityTarget(category));

export const selectedTypeIdsInCategory = (
    targets: ActivityTarget[],
    category: ActivityCategory,
    activityTypes: ActivityTypeOption[],
): TaskTypeId[] =>
    targets.flatMap(target =>
        target.kind === "taskType" &&
        categoryOfType(activityTypes, target.taskTypeId) === category
            ? [target.taskTypeId]
            : [],
    );

export const selectedArchivedTypesInCategory = (
    targets: ActivityTarget[],
    category: ActivityCategory,
    activityTypes: ActivityTypeOption[],
): ActivityTypeOption[] =>
    activityTypes
        .filter(
            option =>
                option.isArchived &&
                option.category === category &&
                targetsInclude(targets, taskTypeActivityTarget(option.taskTypeId)),
        )
        .sort(byTypeName);

export const withoutCategory = (
    targets: ActivityTarget[],
    category: ActivityCategory,
    activityTypes: ActivityTypeOption[],
): ActivityTarget[] =>
    targets.filter(target => categoryOfTarget(target, activityTypes) !== category);

const replaceCategoryTargets = (
    targets: ActivityTarget[],
    category: ActivityCategory,
    activityTypes: ActivityTypeOption[],
    replacements: ActivityTarget[],
): ActivityTarget[] => {
    const remaining = withoutCategory(targets, category, activityTypes);
    const firstIndex = targets.findIndex(
        target => categoryOfTarget(target, activityTypes) === category,
    );
    const insertAt = firstIndex < 0 ? remaining.length : firstIndex;
    return [
        ...remaining.slice(0, insertAt),
        ...replacements,
        ...remaining.slice(insertAt),
    ];
};

export const selectWholeCategory = (
    targets: ActivityTarget[],
    category: ActivityCategory,
    activityTypes: ActivityTypeOption[],
): ActivityTarget[] =>
    replaceCategoryTargets(targets, category, activityTypes, [
        categoryActivityTarget(category),
    ]);

export const toggleWholeCategory = (
    targets: ActivityTarget[],
    category: ActivityCategory,
    activityTypes: ActivityTypeOption[],
): ActivityTarget[] =>
    isWholeCategorySelected(targets, category)
        ? withoutCategory(targets, category, activityTypes)
        : selectWholeCategory(targets, category, activityTypes);

export const toggleTypeInCategory = (
    targets: ActivityTarget[],
    category: ActivityCategory,
    taskTypeId: TaskTypeId,
    activityTypes: ActivityTypeOption[],
): ActivityTarget[] => {
    const scoped = selectedTypeIdsInCategory(targets, category, activityTypes);
    const next = scoped.includes(taskTypeId)
        ? scoped.filter(id => id !== taskTypeId)
        : [...scoped, taskTypeId];
    if (next.length === 0) {
        return withoutCategory(targets, category, activityTypes);
    }
    return replaceCategoryTargets(
        targets,
        category,
        activityTypes,
        next.map(taskTypeActivityTarget),
    );
};

export const resolveTargetLabel = (
    target: ActivityTarget,
    taskTypeNamesById: Map<string, string>,
): string => {
    if (target.kind === "category") {
        return activityCategoryLabels[target.category];
    }
    return taskTypeNamesById.get(target.taskTypeId) ?? unknownActivityTypeLabel;
};

const targetView = (
    target: ActivityTarget,
    activityTypes: ActivityTypeOption[],
): ActivityTargetView => {
    if (target.kind === "category") {
        return {
            kind: "category",
            category: target.category,
            taskTypeId: null,
            label: activityCategoryLabels[target.category],
        };
    }
    const option = activityTypes.find(
        candidate => candidate.taskTypeId === target.taskTypeId,
    );
    return {
        kind: "taskType",
        category: option ? option.category : "other",
        taskTypeId: target.taskTypeId,
        label: option ? option.name : unknownActivityTypeLabel,
    };
};

export const activityTargetViews = (
    targets: ActivityTarget[],
    activityTypes: ActivityTypeOption[],
): ActivityTargetView[] => targets.map(target => targetView(target, activityTypes));

const categoryTargets = (categories: ActivityCategory[]): ActivityTarget[] =>
    categories.map(categoryActivityTarget);

const tierDefaultTouchpoints: Record<string, EditableTouchpoint[]> = {
    tierA: [
        {
            targets: categoryTargets(["visit"]),
            intervalUnit: "Weekly",
            intervalCount: 1,
        },
        {
            targets: categoryTargets(["email", "call", "sms"]),
            intervalUnit: "Weekly",
            intervalCount: 2,
        },
    ],
    tierB: [
        {
            targets: categoryTargets(["visit"]),
            intervalUnit: "Weekly",
            intervalCount: 2,
        },
        {
            targets: categoryTargets(["email", "call", "sms"]),
            intervalUnit: "Weekly",
            intervalCount: 4,
        },
    ],
    tierC: [
        {
            targets: categoryTargets(["visit"]),
            intervalUnit: "Weekly",
            intervalCount: 4,
        },
        {
            targets: categoryTargets(["email", "call", "sms"]),
            intervalUnit: "Weekly",
            intervalCount: 4,
        },
    ],
    tierD: [
        {
            targets: categoryTargets(["email", "call", "sms", "visit"]),
            intervalUnit: "Weekly",
            intervalCount: 4,
        },
    ],
};

export const suggestedTouchpointsForTier = (tierKey: string): EditableTouchpoint[] =>
    tierDefaultTouchpoints[tierKey] ?? [];

export const parseCadenceCount = (value: string): number | null => {
    const parsed = Number.parseInt(value.trim(), 10);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return null;
    }
    return parsed;
};

export const groupTouchpointsByTier = <T extends TouchpointCadence>(
    touchpoints: T[],
): Map<string, T[]> => {
    const byTier = new Map<string, T[]>();
    for (const touchpoint of touchpoints) {
        const existing = byTier.get(touchpoint.tierListItemId);
        if (existing) {
            existing.push(touchpoint);
        } else {
            byTier.set(touchpoint.tierListItemId, [touchpoint]);
        }
    }
    return byTier;
};
